import Cookies from 'js-cookie';

import {
  buildAddToCartEvent,
  mapConsentToSignals,
  mintEventId,
  pushAddToCart,
  readClickIds,
  resolvePlanTier,
  resolvePurchaseValue
} from './dataLayer';
import { sendCapiEvent } from '@lib/api/capi-shim';
import { ConsentManager } from '@lib/consent';

/**
 * FFH-991 — fire an Add to Cart event on BOTH rails (Pattern A). Mints ONE
 * event_id shared by the dataLayer push (read by the GTM pixel) and the CAPI
 * shim, so the two rails dedup on a single id. `plan_tier` / `value` ride along
 * when the plan is known (it is, at checkout).
 *
 * Idempotent: once per browser-tab session. Fail-soft.
 */
export const fireAddToCart = (params: {
  planId?: string | number;
  email?: string;
  externalId?: string;
}): void => {
  if (typeof window === 'undefined') return;

  try {
    const idempotencyKey = 'ffh_add_to_cart';
    if (window.sessionStorage.getItem(idempotencyKey)) return;
    window.sessionStorage.setItem(idempotencyKey, 'true');
  } catch {
    // Storage unavailable (private mode / webview) — proceed without the guard.
  }

  try {
    const eventId = mintEventId();
    const consent = mapConsentToSignals(
      new ConsentManager().getCurrentConsent()
    );
    const sourceUrl = window.location.href;

    pushAddToCart(
      buildAddToCartEvent({
        eventId,
        planId: params.planId,
        email: params.email,
        externalId: params.externalId ?? Cookies.get('_aid'),
        click: readClickIds(),
        sourceUrl,
        referrer: document.referrer || undefined,
        consent
      })
    );

    const planTier = resolvePlanTier(params.planId);
    const value = resolvePurchaseValue(params.planId);
    const custom = {
      ...(planTier ? { planTier } : {}),
      ...(value ? { value, currency: 'GBP' } : {})
    };

    void sendCapiEvent({
      eventName: 'add_to_cart',
      eventId,
      sourceUrl,
      consent,
      ...(Object.keys(custom).length > 0 ? { custom } : {})
    });
  } catch {
    // Swallow — analytics must never break checkout.
  }
};
